import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { ChevronDown, Hotel, Loader2 } from "lucide-react";
import { COLORS } from "@/design-system/tokens";
import { Property } from "./FetchProperties";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  EmptySearch,
  PropertyRow,
  SearchField,
  SectionHeader,
} from "./PropertyListRows";

interface SingleSelectPropertyPanelProps {
  properties: Property[];
  /** Which of `properties` belong to the signed-in user's portfolio. */
  portfolioPropertyIds: Set<string>;
  loading: boolean;
  selectedProperty: Property | null;
  onChange: (value: Property | null) => void;
  /**
   * DOM id to portal the dropdown into. Required inside a modal Dialog: the
   * default `document.body` target lands outside the dialog, where its scroll
   * lock swallows wheel events and the list can't be scrolled.
   */
  boundedContainerId?: string;
  /** Lets a host resize itself around the open list. */
  onOpenChange?: (open: boolean) => void;
}

const matches = (property: Property, term: string): boolean => {
  if (!term) return true;
  const t = term.toLowerCase();
  // Optional chaining despite Property typing both as required strings: these
  // come straight off an API response, so the type is a claim about the
  // contract rather than a guarantee about the payload.
  return (
    (property.propertyName?.toLowerCase().includes(t) ?? false) ||
    (property.propertyId?.toLowerCase().includes(t) ?? false)
  );
};

/**
 * Header property picker for the single-select pages (Pricing Audit, OTA
 * Audits, Performance Analysis, Events). Shares the multi-select bar's panel
 * layout and portfolio grouping, but commits on click — there is no checkbox,
 * Apply step or selection cap, so the panel closes as soon as a row is picked.
 */
function SingleSelectPropertyPanel({
  properties,
  portfolioPropertyIds,
  loading,
  selectedProperty,
  onChange,
  boundedContainerId,
  onOpenChange,
}: SingleSelectPropertyPanelProps) {
  const listboxId = useId();
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [search, setSearch] = useState("");
  // -1 means "no keyboard highlight" — the mouse drives selection until an
  // arrow key is pressed, so opening the panel never pre-highlights a row.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const isKeyboardNavRef = useRef(false);

  const { filteredPortfolio, filteredNonPortfolio, flatOptions } =
    useMemo(() => {
      const portfolio: Property[] = [];
      const nonPortfolio: Property[] = [];
      for (const p of properties) {
        if (!matches(p, search)) continue;
        if (portfolioPropertyIds.has(p.propertyId)) portfolio.push(p);
        else nonPortfolio.push(p);
      }
      return {
        filteredPortfolio: portfolio,
        filteredNonPortfolio: nonPortfolio,
        // Both sections in render order, so arrow keys walk the list the way
        // it reads rather than jumping between groups.
        flatOptions: [...portfolio, ...nonPortfolio],
      };
    }, [properties, portfolioPropertyIds, search]);

  // Reset during render, not in an effect, so a shrunken list never paints a stale highlight.
  if (highlightedIndex >= flatOptions.length) setHighlightedIndex(-1);

  // Keyboard-driven highlight only: wheel-scrolling fires onMouseEnter on rows
  // passing under the cursor, and scrolling those back into view would fight
  // the user's scroll.
  useEffect(() => {
    if (!open || highlightedIndex < 0 || !isKeyboardNavRef.current) return;
    document
      .getElementById(`${listboxId}-option-${highlightedIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, open, listboxId]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setSearch("");
      setHighlightedIndex(-1);
    }
  };

  const handleSelect = (property: Property) => {
    onChange(property);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!flatOptions.length) return;
      isKeyboardNavRef.current = true;
      setHighlightedIndex((i) => Math.min(i + 1, flatOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!flatOptions.length) return;
      isKeyboardNavRef.current = true;
      setHighlightedIndex((i) =>
        i === -1 ? flatOptions.length - 1 : Math.max(i - 1, 0)
      );
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && flatOptions[highlightedIndex]) {
        e.preventDefault();
        handleSelect(flatOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const renderRow = (property: Property, index: number) => (
    <PropertyRow
      key={property.propertyId}
      property={property}
      isMobile={false}
      showCheckbox={false}
      optionId={`${listboxId}-option-${index}`}
      highlighted={index === highlightedIndex}
      checked={selectedProperty?.propertyId === property.propertyId}
      onMouseEnter={() => {
        isKeyboardNavRef.current = false;
        setHighlightedIndex(index);
      }}
      onClick={() => handleSelect(property)}
    />
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex h-10 w-full min-w-0 items-center justify-between gap-1 rounded-[12px] border bg-white px-[6px] shadow-card transition-all duration-150 focus-visible:outline-none hover:shadow-cardHover ${
            open
              ? "border-primary ring-4 ring-primary/15"
              : "border-slate-200 hover:border-slate-300 focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15"
          }`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {loading ? (
              <Loader2
                size={14}
                className="shrink-0 animate-spin text-slate-400"
              />
            ) : (
              <Hotel size={15} className="shrink-0 text-slate-400" />
            )}
            {selectedProperty ? (
              <span className="inline-flex min-w-0 items-center gap-1.5 overflow-hidden rounded-full bg-primary-light px-2 py-0.5 text-[0.8125rem] font-semibold text-primary">
                <span className="h-1 w-1 shrink-0 rounded-full bg-primary" />
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {selectedProperty.propertyName || selectedProperty.propertyId}
                </span>
              </span>
            ) : (
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[0.8125rem] font-normal text-slate-400">
                Select a property
              </span>
            )}
          </div>
          <ChevronDown
            size={15}
            className={`shrink-0 text-slate-400 transition-transform duration-150 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        style={{ width: "var(--radix-popover-trigger-width)" }}
        className="mt-1 flex flex-col overflow-hidden rounded-[14px] border border-slate-200 bg-white shadow-dialog"
      >
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setHighlightedIndex(-1);
          }}
          isMobile={false}
          autoFocus
          onKeyDown={handleKeyDown}
          ariaProps={{
            role: "combobox",
            "aria-expanded": true,
            "aria-controls": listboxId,
            "aria-activedescendant":
              highlightedIndex >= 0
                ? `${listboxId}-option-${highlightedIndex}`
                : undefined,
          }}
        />

        <div
          id={listboxId}
          role="listbox"
          aria-label="Properties"
          className="max-h-[280px] overflow-y-auto overflow-x-hidden py-0.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb [&::-webkit-scrollbar-track]:bg-transparent"
        >
          {flatOptions.length === 0 ? (
            <EmptySearch isMobile={false} />
          ) : (
            <>
              {filteredPortfolio.length > 0 && (
                <div
                  className="mx-0.5 mb-1 rounded-[10px]"
                  style={{ backgroundColor: COLORS.primaryLight }}
                >
                  <SectionHeader
                    label="My Portfolio"
                    count={filteredPortfolio.length}
                    isMobile={false}
                  />
                  {filteredPortfolio.map((p, i) => renderRow(p, i))}
                </div>
              )}
              {filteredNonPortfolio.length > 0 && (
                <>
                  {filteredPortfolio.length > 0 && (
                    <>
                      <div className="mx-[6px] my-0.5 border-t border-slate-100" />
                      <SectionHeader
                        label="Other Properties"
                        count={filteredNonPortfolio.length}
                        isMobile={false}
                      />
                    </>
                  )}
                  {filteredNonPortfolio.map((p, i) =>
                    renderRow(p, filteredPortfolio.length + i)
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-2 shadow-stickyBar">
          <span
            className="min-w-0 truncate text-[0.75rem] text-slate-400"
            style={
              selectedProperty
                ? { fontFamily: "'Roboto Mono', monospace" }
                : undefined
            }
          >
            {selectedProperty ? selectedProperty.propertyId : "None selected"}
          </span>
          {selectedProperty && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[0.8125rem] font-semibold text-slate-600 transition-colors hover:border-slate-800 hover:bg-slate-800 hover:text-white"
            >
              Clear selection
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SingleSelectPropertyPanel;
